import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AI_PURCHASE_ANALYTICS_DEFAULT_DAYS } from '../../../constants/api.constants';

function getDefaultDateRange(): { fromDate: string; toDate: string } {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - AI_PURCHASE_ANALYTICS_DEFAULT_DAYS);
    return { fromDate: fromDate.toISOString(), toDate: toDate.toISOString() };
}

/**
 * Reads/writes the `fromDate`/`toDate` query params shared across all 3 AI Purchase
 * Numbers pages, so the selected date range survives navigation between them and the
 * browser back/forward buttons (plan doc rework §5 — date range must live in the URL,
 * not component-local state).
 *
 * Defaults to the last 15 days on first load of any of the 3 pages if the params are
 * missing from the URL (e.g. a fresh visit to `/ai-purchase-numbers`), by writing the
 * defaults into the URL (replacing the current history entry, not pushing a new one).
 */
export function useAIPurchaseDateRangeParams() {
    const [searchParams, setSearchParams] = useSearchParams();

    const fromDateParam = searchParams.get('fromDate');
    const toDateParam = searchParams.get('toDate');

    useEffect(() => {
        if (!fromDateParam || !toDateParam) {
            const defaults = getDefaultDateRange();
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (!next.get('fromDate')) next.set('fromDate', defaults.fromDate);
                    if (!next.get('toDate')) next.set('toDate', defaults.toDate);
                    return next;
                },
                { replace: true }
            );
        }
        // Only run when the params are actually missing — see fromDateParam/toDateParam deps.
    }, [fromDateParam, toDateParam, setSearchParams]);

    /**
     * Commits both dates to the URL at once (this is what actually triggers each
     * page's fetch-on-mount `useEffect`, since it's keyed on `fromDate`/`toDate`) —
     * called only when the user clicks "Apply" in `AIPurchaseDateRangeControls`,
     * never on individual date picks.
     */
    const setDateRange = useCallback(
        (newFromDate: string, newToDate: string) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('fromDate', newFromDate);
                    next.set('toDate', newToDate);
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const defaults = useMemo(getDefaultDateRange, []);

    return {
        fromDate: fromDateParam ?? defaults.fromDate,
        toDate: toDateParam ?? defaults.toDate,
        /** Raw query string (e.g. `fromDate=...&toDate=...`) to preserve when building links to other pages. */
        queryString: searchParams.toString(),
        setDateRange,
    };
}
